<template lang="pug">
    .play-box
        template(v-if='!isLoading')
            game-preparation-counter(v-if='!gameStarted && counter' :counter="counter")
            template(v-else="")
                game-header(
                    @soundStateChanged ="headerKey = !headerKey"
                    :key="headerKey"
                    :slug='type',
                    :game-type='parameters && parameters.courseType',
                    :is-homework="!!exerciseId",
                )
                .play-box__game(v-if='gameStarted && !gameFinished || showUnselectedWords')
                    .play-box__example
                        .play-box__header(:class="{'play-box__before-start-game-header': parameters.withTime && showNeededWords, 'mobile-version': isMobile}")
                            .play-box__task
                                .play-box__title
                                    span(v-if='parameters.withTime && showNeededWords')
                                        | {{ $i18n.t('trainer.trainer_game_wordField_rememberWords') }}
                                    span(v-else-if="!parameters.withTime")
                                        | {{ $i18n.t('trainer.trainer_game_wordField_findThisWords') }}
                                    span(v-else)
                                        | {{ $i18n.t('trainer.trainer_game_wordField_findWords') }}
                                .play-box__subtitle(
                                    v-if='!parameters.withTime && showNeededWords',
                                    :style="wordStyle"
                                )
                                    span.play-box__word(v-for="word in neededWords")
                                        | {{ word.word }}
                            .play-box__scaling-bar-block
                                scaling-bar(
                                    @setFontSize="setFontSize"
                                    :is-game-tuned="true"
                                    :font-size="fontSize"
                                    :element-name="elementName"
                                    :is-from-shutter="false"
                                    :key="clientWidth"
                                )
                        .play-box__subtitle(
                            v-if='parameters.withTime && showNeededWords',
                            :style="wordStyle"
                        )
                            span.play-box__word(v-for="word in neededWords")
                                | {{ word.word }}
                        template(v-if='parameters.withTime && !showNeededWords || !parameters.withTime')
                            .play-box__buttons.play-box__buttons--game(
                                v-if='showWordField && !showUnselectedWords'
                            )
                                cs-button.play-box__button(
                                    :disabled='selectedWords.length === 0',
                                    @click='processReply'
                                    :style="wordStyle"
                                )
                                    | {{ $i18n.t('trainer.trainer_gamePlayBox_reply') }}
                            .play-box__buttons.play-box__buttons--game(
                                v-if='showUnselectedWords'
                            )
                                cs-button.play-box__button(
                                    @click='showUnselectedWords = false'
                                    :style="wordStyle"
                                )
                                    | {{ $i18n.t('olympiads.olympiads_processStage_further') }}
                        template(v-if='parameters.withTime && !showNeededWords || !parameters.withTime')
                            .play-box__overflow-container
                                .play-box__field(:style="wordStyle")
                                    template(v-if="!isLessThanCalculationsFontSize")
                                        template(v-for='(line, indexOfLine) in displayedLines')
                                            span.play-box__word(
                                                :class="wordClass(word, indexOfLine, indexOfWord)",
                                                v-for='(word, indexOfWord) in line',
                                                @click='selectWord(word, indexOfLine, indexOfWord)'
                                                :style="wordStyle"
                                            )
                                                | {{ word.word }}
                                    template(v-else)
                                        .play-box__words-row(v-for='(line, indexOfLine) in displayedLines')
                                            span.play-box__word(
                                                :class="wordClass(word, indexOfLine, indexOfWord)",
                                                v-for='(word, indexOfWord) in line',
                                                @click='selectWord(word, indexOfLine, indexOfWord)'
                                                :style="wordStyle"
                                            )
                                                | {{ word.word }}
                .play-box__end-container(v-if='gameFinished && !isOlympiad && !showUnselectedWords')
                    fireworks(
                        v-if="gameFinished && !isOlympiad && gameResult === 'success' "
                        :is-mobile="isMobile"
                    )
                    .play-box__reaction-container
                        .play-box__answers-container
                            .play-box__answers.play-box__answers--left
                                | {{ $i18n.t('trainer.trainer_game_wordField_yourTime') }}
                                br
                                | {{ getTime }}
                            .play-box__answers.play-box__answers--right
                                | {{ $i18n.t('trainer.trainer_game_wordField_foundWords') }}
                                br
                                | {{ resultWords.length }} / {{ amountOfWordsToFind }}
                        .play-box__answers.play-box__answers--left.play-box__answers--show-md
                            | {{ $i18n.t('trainer.trainer_game_wordField_yourTime') }}
                            br
                            | {{ getTime }}
                        result-image.play-box__image(:status="gameResult")
                        .play-box__answers.play-box__answers--right.play-box__answers--show-md
                            | {{ $i18n.t('trainer.trainer_game_wordField_foundWords') }}
                            br
                            | {{ resultWords.length }} / {{ amountOfWordsToFind }}
                    result-buttons(
                        :exercise-id="exerciseId"
                        :games-type="parameters.courseType"
                        show-restart-button
                        @restartGame="restartGame"
                    )
                        slot
        cs-loader(v-else)
</template>

<script>
    /*eslint-disable*/
    import WordFieldApi from '../../../../api/games/wordsField';
    import ScalingBar from './ScalingBar.vue';
    import textScrollMixin from '../../../../utils/trainer/textScroll';
    import playBoxGameMixin from '../../../../utils/trainer/playBoxGameMixin.js';
    import olympiadAnswerTimerMixin from '../../../../utils/olympiads/olympiadAnswerTimerMixin.js';
    import GamePreparationCounter from '../../GamePreparationCounter.vue';
    import { chunk, cloneDeep, isEqual, shuffle, sum, uniqBy } from 'lodash';
    import CsButton from '@/elements/CsButton.vue';
    import CsLoader from '@/elements/CsLoader.vue';

    const MAX_AMOUNT_OF_SIMILAR_WORDS = 7;
    const MIN_AMOUNT_OF_SIMILAR_WORDS = 1;
    const AMOUNT_OF_WORDS_IN_ROW = 13;
    const WIDTH_COEFFICIENT = 13;
    const DEFAULT_VOLUME = 0.6;
    const CALCULATIONS_FONT_SIZE = 20;

    function DEFAULT_DATA() {
        return {
            words: [],
            neededWords: [],
            selectedWords: [],
            displayedLines: [],
            showNeededWords: true,
            resultWords: [],
            repetitionsOfWords: {},
            showUnselectedWords: false,
            fontSize: CALCULATIONS_FONT_SIZE,
            amountOfWordsInRow: 1,
            gameResult: 'success',
            clientWidth: 0,
        };
    }

    export default {
        mixins: [textScrollMixin, playBoxGameMixin, olympiadAnswerTimerMixin],
        created() {
            window.addEventListener('resize', this.getClientWidth);
        },
        mounted() {
            this.gameBoxName = 'play-box__field';
        },
        watch: {
            selectedWords(array) {
                if (array.length === 0) {
                    window.removeEventListener('keydown', this.processReplyOnEnter);
                }
            },
        },
        data() {
            return DEFAULT_DATA();
        },
        computed: {
            elementName() {
                if ((this.parameters.withTime && !this.showNeededWords) || !this.parameters.withTime) {
                    return 'play-box__field';
                }
                return 'play-box__subtitle';
            },
            showWordField() {
                return !this.parameters.withTime || (this.parameters.withTime && !this.showNeededWords);
            },
            amountOfWordsToFind() {
                return sum(Object.values(this.repetitionsOfWords));
            },
            showGameHeader() {
                if (this.parameters.withTime && !this.showNeededWords) {
                    return true;
                }
                return (
                    (!this.parameters.withTime && !this.gameFinished && this.gameStarted) || this.showUnselectedWords
                );
            },
            wordStyle() {
                return {
                    'font-size': +this.fontSize + 'px',
                    'line-height': +this.fontSize + 8 + 'px',
                };
            },
            isLessThanCalculationsFontSize() {
                return Number(this.fontSize) <= CALCULATIONS_FONT_SIZE;
            },
        },
        methods: {
            getClientWidth() {
                this.clientWidth = document.documentElement.clientWidth;
                if (this.clientWidth < 576 && this.fontSize > 30) {
                    this.setFontSize(30);
                } else if (this.clientWidth < 766 && this.fontSize > 40) {
                    this.setFontSize(40);
                } else if (this.clientWidth < 1200 && this.fontSize > 60) {
                    this.setFontSize(60);
                } else if (this.clientWidth < 1430 && this.fontSize > 80) {
                    this.setFontSize(80);
                } else if (this.clientWidth < 1920 && this.fontSize > 100) {
                    this.setFontSize(100);
                } else if (this.clientWidth >= 1920) {
                    this.setFontSize(150);
                }
            },
            fetchData() {
                this.getExercise()
                    .then(() => {
                        WordFieldApi.getWords({
                            locale: this.user.locale,
                            groups: this.parameters.selectedGroups,
                            amountOfWords: this.parameters.amountOfRows * AMOUNT_OF_WORDS_IN_ROW,
                        })
                            .then(data => {
                                this.words = Object.values(data).map((word, index) => {
                                    word.word = word.word.replace('&#x301;', '');
                                    word.id = index;
                                    return word;
                                });
                                this.fillNeededWords();
                                this.registerSounds();
                            })
                            .catch(e => this.showErrorMessage(e));
                    })
                    .catch(e => {
                        this.isLoading = false;
                        this.$toasted.error(this.$root.$t('errors.getting_level'));
                        this.$router.push({ name: 'dashboard' });
                    });
            },
            refreshGameStatBeforeSend() {
                let arrayOfUniqAnswers = uniqBy(this.resultWords, 'word');
                this.gameStat.answers = arrayOfUniqAnswers.length;
                this.gameStat.attempts = 1;
                this.gameStat.isCompleted = this.gameFinished;
                this.gameStat.executionTime = this.time;
                this.gameStat.chainOfAttempts = [];
                this.gameStat.isCorrect = true;
                this.gameStat.percent = parseInt((arrayOfUniqAnswers.length / this.neededWords.length) * 100);
                this.gameStat.amountOfWords = this.neededWords.length;
                this.gameStat.parameters = cloneDeep(this.parameters);
            },
            beforeStart() {
                this.startGame();
            },
            startGame() {
                this.gameStarted = true;
                if (this.answerDuration) {
                    this.startAnswerTimer();
                }
                if (this.parameters.withTime) {
                    setTimeout(() => {
                        this.showNeededWords = false;
                        this.startTimer();
                    }, this.parameters.showTime * 1000);
                } else {
                    this.startTimer();
                }
            },
            startTimer() {
                this.$nextTick(() => {
                    if (!this.timer) {
                        this.setTimer();
                    }
                });
            },
            fillNeededWords() {
                const wordLengths = this.words.map(word => word.word.length);
                const averageWordLength = wordLengths.reduce((a, b) => a + b, 0) / this.words.length;
                const maxWordLength = Math.max(...wordLengths);
                const resultLength = Math.ceil((averageWordLength * 2) / 3 + maxWordLength / 3);
                const gameContainerWidth = document.querySelector('.play-box').clientWidth;
                this.amountOfWordsInRow = Math.floor(gameContainerWidth / WIDTH_COEFFICIENT / (resultLength + 2));
                this.neededWords = this.words.slice(0, this.parameters.amountOfWords);
                let newWords = [];
                while (!this.neededWords.every(element => newWords.includes(element))) {
                    newWords = [];
                    this.words.forEach(word => {
                        let length = this.getRandomInt(
                            MIN_AMOUNT_OF_SIMILAR_WORDS,
                            this.isMobile ? 3 : MAX_AMOUNT_OF_SIMILAR_WORDS
                        );
                        for (let i = 0; i < length; i++) {
                            if (newWords.length >= this.amountOfWordsInRow * this.parameters.amountOfRows) {
                                return;
                            }
                            newWords.push(word);
                        }
                    });
                }
                this.words = newWords;
                this.displayedLines = chunk(shuffle(this.words), this.amountOfWordsInRow);
                this.words.forEach(element => {
                    if (this.neededWords.includes(element)) {
                        if (this.repetitionsOfWords[element.word]) {
                            ++this.repetitionsOfWords[element.word];
                        } else {
                            this.repetitionsOfWords[element.word] = 1;
                        }
                    }
                });
            },
            getRandomInt(min, max) {
                return Math.floor(Math.random() * (max - min)) + min;
            },
            selectWord(word, indexOfLine, indexOfWord) {
                if (!this.gameFinished) {
                    let index = this.selectedWords.findIndex(element =>
                        isEqual(element, {
                            item: word,
                            line: indexOfLine,
                            row: indexOfWord,
                        })
                    );
                    if (index !== -1) {
                        this.selectedWords.splice(index, 1);
                    } else {
                        if (this.selectedWords.length === this.amountOfWordsToFind) {
                            return;
                        }
                        this.selectedWords.push({ item: word, line: indexOfLine, row: indexOfWord });
                    }
                    window.addEventListener('keydown', this.processReplyOnEnter);
                }
            },
            isWordSelected(word, indexOfLine, indexOfWord) {
                return this.selectedWords.find(element => {
                    return element.line === indexOfLine && element.row === indexOfWord;
                });
            },
            isNeededWordUnselected(word, line, row) {
                if (this.gameFinished) {
                    return this.neededWords.find(item => {
                        if (this.areWordsEqual(item, word) && !this.isWordSelected(word, line, row)) {
                            return true;
                        }
                    });
                }
            },
            isUnwantedWordSelected(word, line, row) {
                if (this.gameFinished) {
                    let isWordNeeded = false;
                    this.neededWords.forEach(item => {
                        if (this.areWordsEqual(item, word)) {
                            isWordNeeded = true;
                        }
                    });
                    return !isWordNeeded && this.isWordSelected(word, line, row);
                }
            },
            processReply() {
                this.stopTimer();
                window.removeEventListener('keydown', this.processReplyOnEnter);
                let neededWords = this.neededWords.map(item => {
                    return { id: item.id, word: item.word };
                });
                let selectedWords = this.selectedWords.map(element => {
                    return { id: element.item.id, word: element.item.word };
                });
                this.resultWords = selectedWords.filter(item => {
                    for (let i = 0; i < neededWords.length; i++) {
                        if (this.areWordsEqual(item, neededWords[i])) {
                            return item;
                        }
                    }
                });
                if (this.resultWords.length < Math.ceil(this.amountOfWordsToFind / 2)) {
                    this.gameResult = 'failure';
                }
                if (this.resultWords.length < this.amountOfWordsToFind) {
                    this.showUnselectedWords = true;
                    setTimeout(() => window.addEventListener('keydown', this.finishGameOnEnter), 100);
                }
                this.gameFinished = true;
                this.sendStat();
            },
            areWordsEqual(first, second) {
                return first.id === second.id || first.word.trim() === second.word.trim();
            },
            setFontSize(fontSize) {
                this.fontSize = fontSize;
            },
            processReplyOnEnter(event) {
                if (event.keyCode === 13) {
                    this.processReply();
                }
            },
            finishGameOnEnter(event) {
                if (event.keyCode === 13) {
                    this.showUnselectedWords = false;
                }
            },
            beforeGameRestart() {
                Object.assign(this, DEFAULT_DATA());
            },
            wordClass(word, indexOfLine, indexOfWord) {
                return {
                    'play-box__word--selected': this.isWordSelected(word, indexOfLine, indexOfWord),
                    'play-box__word--unselected':
                        (this.isNeededWordUnselected(word, indexOfLine, indexOfWord) ||
                            this.isUnwantedWordSelected(word, indexOfLine, indexOfWord)) &&
                        !this.isOlympiad,
                    'play-box__word--inverted': this.parameters.invertedWords,
                };
            },
        },
        beforeDestroy() {
            window.removeEventListener('keydown', this.finishGameOnEnter);
            window.removeEventListener('resize', this.getClientWidth);
        },
        components: {
            CsLoader,
            CsButton,
            GamePreparationCounter,
            ScalingBar,
        },
    };
</script>

<style lang="scss" scoped>
    @import '@/assets/sass/games/playbox.scss';

    .mobile-version {
        flex-direction: column;
        align-items: center;
        text-align: center;
    }

    .play-box--warning {
        display: table;
    }

    .play-box__game {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column-reverse;
        @include media($lg) {
            flex-direction: row;
        }
    }

    .play-box__example {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        flex-grow: 5;
        flex-direction: column;
    }

    .play-box__overflow-container {
        overflow-y: auto;
        @include scrollbar;
    }

    .play-box__field {
        padding: 0 20px;
        text-align: center;
        font-size: 18px;
        user-select: none;
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
    }

    .play-box__words-row {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
    }

    .play-box__answer-container {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .answer-container__header {
        margin-bottom: 20px;
        font-weight: 500;
        font-size: 32px;
        line-height: 38px;
    }

    .answer-container__input .cs-input {
        margin-bottom: 0;
        font-size: 52px;
        line-height: 62px;
        padding: 9px 15px;
        text-align: center;
        max-width: 330px;
    }

    .play-box__word {
        padding: 0 8px;
        margin: 0 10px 3px 0;
        cursor: pointer;
    }

    .play-box__word:last-child {
        margin-right: 0;
    }

    .play-box__word--selected {
        color: $x-color-white;
        background: $hsl-color-green-58;
    }

    .play-box__word--unselected {
        color: $x-color-white;
        background: $hsl-color-red-62;
    }

    .play-box__word--inverted {
        display: inline-block;
        transform: rotate(180deg);
        margin-right: 10px;
    }

    .play-box__header {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        font-weight: 500;
        font-size: 28px;
        line-height: 34px;
        margin-bottom: 20px;
    }

    .play-box__before-start-game-header {
        flex-direction: column;
    }

    .play-box__task {
        align-self: center;
    }

    .play-box__title {
        margin-bottom: 20px;
        text-align: center;
    }

    .play-box__scaling-bar-block {
        margin-left: 20px;
        @include media($xlg) {
            margin-left: 40px;
        }
    }

    .play-box__subtitle {
        display: flex;
        flex-wrap: wrap;
        margin-bottom: 0;
        justify-content: center;
    }

    .play-box__end-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    .play-box__reaction-container {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: row;
        width: 100%;
    }

    .play-box__answers {
        font-weight: 500;
        font-size: 22px;
        line-height: 26px;
        text-align: center;
        @include media($lg) {
            font-size: 32px;
            line-height: 38px;
        }
        @include media($xl) {
            font-size: 42px;
            line-height: 50px;
        }
    }

    .play-box__answers--left {
        color: $hsl-color-green-58;
        margin-left: 20px;
        @include media($lg) {
            margin-left: 35px;
        }
    }

    .play-box__answers--right {
        color: $hsl-color-green-58;
        margin-right: 20px;
        @include media($lg) {
            margin-right: 35px;
        }
    }

    .play-box__answers--show-md {
        display: block;
    }

    .play-box__answers-container {
        display: none;
    }

    .play-box__image {
        max-width: 100%;
        margin-bottom: 40px;
        @media screen and (max-width: 1200px) {
            height: 400px;
        }
    }

    .play-box__buttons {
        display: flex;
        flex-direction: column;
        margin-top: 25px;
        @include media($lg) {
            flex-direction: row;
        }
    }

    .play-box__buttons--game {
        margin: 0 0 25px;
        justify-content: center;
    }

    .play-box__button {
        margin: 20px 0;
        @include media($lg) {
            margin: 0 20px;
        }
    }

    .play-box__button:first-child {
        margin-top: 0;
        @include media($lg) {
            margin-left: 0;
        }
    }

    .play-box__button:last-child {
        margin-bottom: 0;
        @include media($lg) {
            margin-right: 0;
        }
    }

    .play-box__game-info {
        align-self: flex-end;
        @include media($lg) {
            align-self: auto;
        }
    }

    .warning {
        display: table-cell;
        vertical-align: middle;
        text-align: center;
    }
</style>
